/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * MA6200-G1 Platform Configuration
 * Defines MA6200-G1-specific behavior for topology display and interaction
 */

import { IInventoryGraphNode, IRemoteEquipment } from "../../../store/api/devices/types";
import { PlatformConfig } from "./PlatformConfig";
import { ma6200g1HierarchyBuilder } from "../builders/MA6200G1HierarchyBuilder";

export class MA6200G1PlatformConfig implements PlatformConfig {
  platformId = "ma6200g1";
  displayName = "MA6200-G1";
  hierarchyBuilder = ma6200g1HierarchyBuilder;

  matches(platformId: string): boolean {
    const normalized = platformId?.toLowerCase().replace(/[-_\s]/g, "") || "";
    return normalized.includes("ma6200g1") || normalized.includes("ma6200g1");
  }

  findDeviceForNode(
    nodeId: string,
    node: IInventoryGraphNode | undefined,
    listData: IRemoteEquipment[],
    rootId: string
  ): IRemoteEquipment | null {
    // For MA6200-G1, the root node IS the primary A2, so just find it by objectKey
    // The primary A2 has objectKey ending with "_0_0.0.0.0"
    const device = listData.find((d) => d.objectKey === nodeId || d.managedObject === nodeId);
    if (device) {
      console.log("[MA6200-G1] Found device:", device.objectKey, device.equipmentType);
      return device;
    }

    // Fallback: try matching by serial number or other fields
    if (node) {
      let fallbackDevice = listData.find((d) => d.serialNumber === node.label);
      if (fallbackDevice) {
        console.log("[MA6200-G1] Found device by serialNumber (FALLBACK):", fallbackDevice.serialNumber);
        return fallbackDevice;
      }

      // Try IP address
      if (node.ipAddress) {
        fallbackDevice = listData.find((d) => d.ipAddress === node.ipAddress);
        if (fallbackDevice) {
          console.log("[MA6200-G1] Found device by ipAddress (FALLBACK):", fallbackDevice.ipAddress);
          return fallbackDevice;
        }
      }
    }

    console.warn("[MA6200-G1] No device found for node:", nodeId, node);
    return null;
  }

  getRootLedColor(listData: IRemoteEquipment[]): string {
    // Find primary A2 (AU2) with devId=0 and route=0.0.0.0
    const primaryA2 = listData.find((d) => {
      if (!d.objectKey) return false;
      const parts = d.objectKey.split("_");
      const devId = parts[1];
      const route = parts.slice(2).join("_");
      const equipmentType = d.equipmentType?.toUpperCase() || "";
      
      return (
        (equipmentType === "AU2" || equipmentType === "A2" || equipmentType.includes("AU2")) &&
        devId === "0" &&
        route === "0.0.0.0"
      );
    });

    if (!primaryA2) return "#9e9e9e"; // Gray if no primary A2 found

    // Simple status-based LED color for MA6200-G1
    const status = primaryA2.status?.toString().toLowerCase() || "";
    
    if (status === "connected" || status === "online") {
      return "#4caf50"; // Green - online
    } else if (status === "disconnected" || status === "offline") {
      return "#f44336"; // Red - disconnected
    } else if (status === "warning" || status === "minor") {
      return "#ff9800"; // Orange - warning
    } else if (status === "critical" || status === "major") {
      return "#f44336"; // Red - critical
    }
    
    return "#9e9e9e"; // Gray - unknown
  }

  metadata = {
    rootEquipmentType: "AU2",
    primaryA2Criteria: {
      devId: "0",
      route: "0.0.0.0",
    },
  };
}
